import {
  View, ActivityIndicator, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, Text,
  TextInput,
} from 'react-native';
import React, { useEffect, useState } from 'react';
import { FontAwesome } from '@expo/vector-icons';
import * as Sentry from '@sentry/react-native';
import { useSelector } from 'react-redux';
import { RootTabScreenProps } from '../types';
import Colors from '../constants/Colors';
import isMobile from '../components/IsMobile';
import SupportPeopleLogo from '../components/SupportPeopleLogo';
import Style from '../constants/Style';
import { logEvent } from '../components/LogEvent';
import CustomTextField from '../components/CustomTextField';
import sendSupportRequest from '../requests/Support/SendSupportRequest';
import { emailRegex } from '../components/Regex';
import PressableStyled from '../components/PressableStyled';

type Topic = { name: string, issues?: { name: string }[] };

// eslint-disable-next-line no-unused-vars
export default function SupportScreen({ navigation: { addListener, navigate, pop }, route }: RootTabScreenProps<'Support'>) {
  const [topicDropdownOpen, setTopicDropdownOpen] = useState<boolean>(false);
  const [selectedTopic, setSelectedTopic] = useState<Topic>(null);
  const [issueDropdownOpen, setIssueDropdownOpen] = useState<boolean>(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueFocused, setIssueFocused] = useState<boolean>(false);
  const [issueText, setIssueText] = useState<string>('');
  const [emailText, setEmailText] = useState<string>('');
  const [emailInvalidMessage, setEmailInvalidMessage] = useState<string>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { firstName } = useSelector((state: { user: { firstName } }) => state.user);
  const [signInHelp, setSignInHelp] = useState<boolean>(false);

  const showProblemDropdown: boolean = !!selectedTopic && selectedTopic.name !== 'Can\'t login'
    && selectedTopic.name !== 'Other' && !!selectedTopic?.issues?.length;
  const disabled: boolean = !selectedTopic || loading || (signInHelp && !emailText)
    || (showProblemDropdown && !selectedIssue);

  const handleClick = () => {
    setTopicDropdownOpen(false);
    setIssueDropdownOpen(false);
  };

  useEffect(() => {
    const unsubscribe = addListener('focus', () => {
      setSubmitted(false);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    setSignInHelp((route?.params as any)?.signInHelp);
  }, [route]);

  useEffect(() => setEmailInvalidMessage(null), [emailText]);

  const getBorderColor = () => (issueFocused
    ? Colors.light.hereworksBlue300 : Colors.light.hereworksBlue100);

  const content = () => (<KeyboardAvoidingView style={styles.container}
    behavior={'position'}>
    <ScrollView style={{}} contentContainerStyle={{
      flex: isMobile ? undefined : 1, justifyContent: 'center',
    }}>
      <View style={[styles.mainView, Platform.OS === 'web' ? styles.shadow : {}]}>
        {submitted ? <>
          <SupportPeopleLogo />
          <Text style={[
            styles.mainTitle,
            { marginTop: 32, marginBottom: 12, textAlign: 'center' }]}>
            Thank you for feedback</Text>
          <Text style={[styles.secondSub, { flex: 1, textAlign: 'center' }]}>
            {'Thank you for submitting your support ticket! Our team is on it. We\'ll resolve your issue or provide assistance as soon as possible.'}</Text>
          {Platform.OS === 'web' ? <></> : <Pressable
            style={(state) => [
              { opacity: !selectedTopic ? 0.5 : 1 }, styles.sendButton,
              state.hovered && Style.hovered, state.pressed && Style.pressed]}
            disabled={!selectedTopic}
            onPress={() => {
              logEvent('Support', 'back_pressed');
              if (signInHelp) {
                pop();
              } else {
                navigate('Home');
              }
            }}
          ><Text style={styles.sendText}>{
            signInHelp ? 'Back to login' : 'Back to home page'}</Text></Pressable>}
        </>
          : <>
            {Platform.OS === 'web' ? <Text style={styles.webTitle}>Request Support</Text> : <></>}
            {firstName ? <Text style={[styles.secondTitle, { marginTop: 16 }]}>
              {`👋 Hi ${firstName}`}
            </Text> : <></>}
            <Text style={styles.secondTitle}>
              What problem have you faced?
            </Text>
            <Text style={styles.subTitle}>
              {'Your feedback is valuable to us, please tell us more...'}
            </Text>
            <View style={{ alignSelf: 'stretch', zIndex: 2 }}>
              <Text style={styles.dropdownTitle}>{'Topic *'}</Text>
              <View style={{ zIndex: 4 }}>
                {topicDropdownOpen ? <View style={Style.dropdown}>
                  <Pressable
                    style={{
                      flex: 1, alignItems: 'flex-end', flexDirection: 'row',
                    }}
                    onPress={() => {
                      logEvent('Support', 'close_topic_dropdown');
                      setTopicDropdownOpen(false);
                    }}
                  >
                    {selectedTopic?.name?.length > 0
                      ? <Text
                        style={[
                          styles.placeholder,
                          { color: Colors.light.hereworksBlack600, flex: 1 },
                        ]}>
                        {selectedTopic?.name}</Text>
                      : <Text style={[styles.placeholder, { flex: 1 }]}>{'Choose topic'}</Text>}
                    <FontAwesome size={20} style={styles.icon} name={'angle-up'} />
                  </Pressable>
                </View> : <Pressable
                  style={Style.dropdown}
                  onPress={() => {
                    logEvent('Support', 'open_topic_dropdown');
                    setTopicDropdownOpen(!topicDropdownOpen);
                    setIssueDropdownOpen(false);
                  }}
                >
                  {selectedTopic?.name?.length > 0
                    ? <Text style={[styles.placeholder, { color: Colors.light.hereworksBlack600 }]}>
                      {selectedTopic?.name}</Text>
                    : <Text style={styles.placeholder}>{'Choose topic'}</Text>}
                  <View style={{ flex: 1, alignItems: 'flex-end' }}>
                    <View>
                      <FontAwesome size={20} style={styles.icon} name={'angle-down'} />
                    </View>
                  </View>
                </Pressable>}
                {topicDropdownOpen
                  && <View style={styles.dropdownView}>
                    <ScrollView style={styles.scrollContainer} nestedScrollEnabled={true}>
                      {TOPICS.map((topic: any, key: number) => {
                        if (!signInHelp && key === 0) {
                          return null;
                        }
                        return (<Pressable
                          key={key}
                          onPress={() => {
                            logEvent('Support', 'selected_topic');
                            setTopicDropdownOpen(false);
                            setSelectedTopic(topic);
                            setSelectedIssue(null);
                          }}
                          style={(state) => [
                            styles.dropdownButton,
                            {
                              backgroundColor: (
                                state.hovered || state.pressed)
                                ? Colors.light.hereworksWhite200 : undefined,
                            },
                          ]}
                        ><Text>{topic.name}</Text></Pressable>);
                      })}
                    </ScrollView></View>}
              </View>
              {showProblemDropdown && <View style={{ zIndex: 3 }}>
                <Text style={[styles.dropdownTitle, { marginTop: 16 }]}>
                  {'What is the problem?'}
                </Text>
                <View style={{ zIndex: 2 }}>
                  {issueDropdownOpen ? <View style={[Style.dropdown, { zIndex: 2 }]}>
                    <Pressable
                      style={{
                        flex: 1, alignItems: 'flex-end', zIndex: 1, flexDirection: 'row',
                      }}
                      onPress={() => {
                        logEvent('Support', 'close_issue_dropdown');
                        setIssueDropdownOpen(false);
                      }}
                    >
                      <View style={{ flex: 1, zIndex: 1, flexDirection: 'row' }}>
                        {selectedIssue?.name?.length > 0
                          ? <Text style={[
                            styles.placeholder,
                            { color: Colors.light.hereworksBlack600, flex: 1 },
                          ]}>
                            {selectedIssue?.name}</Text>
                          : <Text style={[styles.placeholder, { flex: 1 }]}>{'Choose problem'}</Text>}
                        <FontAwesome size={20} style={styles.icon} name={'angle-up'} />
                      </View>
                    </Pressable>
                  </View> : <Pressable
                    style={[Style.dropdown, { zIndex: 1 }]}
                    onPress={() => {
                      logEvent('Support', 'open_issue_dropdown');
                      setIssueDropdownOpen(!issueDropdownOpen);
                      setTopicDropdownOpen(false);
                    }}
                  >
                    {selectedIssue?.name?.length > 0
                      ? <Text style={[
                        styles.placeholder, { color: Colors.light.hereworksBlack600 },
                      ]}>
                        {selectedIssue?.name}</Text>
                      : <Text style={styles.placeholder}>{'Choose problem'}</Text>}
                    <View style={{ flex: 1, alignItems: 'flex-end' }}>
                      <View>
                        <FontAwesome size={20} style={styles.icon} name={'angle-down'} />
                      </View>
                    </View>
                  </Pressable>}
                  {issueDropdownOpen
                    && <View style={[styles.dropdownView, { zIndex: 2 }]}>
                      <ScrollView style={styles.scrollContainer} nestedScrollEnabled={true}>
                        {selectedTopic?.issues?.map((issue: any, key: number) => <Pressable
                          key={key}
                          onPress={() => {
                            logEvent('Support', 'selected_issue');
                            setIssueDropdownOpen(false);
                            setSelectedIssue(issue);
                          }}
                          style={(state) => [
                            styles.dropdownButton,
                            {
                              backgroundColor: (
                                state.hovered || state.pressed)
                                ? Colors.light.hereworksWhite200 : undefined,
                            },
                          ]}
                        ><Text>{issue.name}</Text></Pressable>)}
                      </ScrollView></View>}
                </View>
              </ View>}
              {signInHelp ? <View style={{ zIndex: 2 }}>
                <Text style={[styles.dropdownTitle, { marginTop: 16 }]}>{'Email *'}</Text>
                <CustomTextField
                  value={emailText}
                  updateValue={setEmailText}
                  placeholder='Email'
                  invalid={!!emailInvalidMessage}
                  errorText={emailInvalidMessage}
                  required
                />
              </View> : <></>}
              <View style={{ zIndex: 1 }}>
                <Text style={[styles.dropdownTitle, { marginTop: 16 }]}>{'Issue description'}</Text>
                <TextInput
                  onBlur={() => setIssueFocused(false)}
                  onFocus={() => setIssueFocused(true)}
                  value={issueText}
                  onChangeText={(text) => setIssueText(text)}
                  style={[
                    styles.inputText,
                    { borderColor: getBorderColor() },
                  ]}
                  multiline={true}
                  placeholderTextColor={Colors.light.hereworksBlack400}
                  placeholder='Enter' />
              </View>
            </View>
            <Pressable
              style={[{ opacity: disabled ? 0.5 : 1 }, styles.sendButton]}
              {...{ disabled }}
              onPress={() => {
                if (signInHelp && !emailRegex.exp.test(emailText?.trim())) {
                  setEmailInvalidMessage(emailRegex.message);
                  return;
                }
                logEvent('Support', 'send_pressed');
                setLoading(true);
                sendSupportRequest(
                  selectedTopic?.name,
                  showProblemDropdown ? selectedIssue?.name : undefined,
                  issueText,
                  emailText ?? '',
                ).then((result) => {
                  if (!result?.isError) {
                    setSubmitted(true);
                  }
                });
              }}
            >
              <Text style={styles.sendText}>Send</Text>
              {loading ? <View style={StyleSheet.absoluteFill}>
                <ActivityIndicator />
              </View> : <></>}
            </Pressable>
          </>}
      </View>
    </ScrollView>
    <PressableStyled onPress={() => Sentry.captureException('test')}><Text style={{ opacity: 0.2 }}>~</Text></PressableStyled>
  </KeyboardAvoidingView>);

  return Platform.OS === 'web' ? <Pressable
    onPress={handleClick}
    style={{ flex: 1, cursor: 'auto' }}
    children={content()} /> : content();
}

const styles = StyleSheet.create({
  container: { flex: 1, justifyContent: 'center', backgroundColor: '#fff' },
  mainView: {
    backgroundColor: '#fff',
    alignSelf: 'center',
    borderRadius: isMobile ? 0 : 10,
    padding: isMobile ? 24 : 40,
    alignItems: 'center',
    flex: isMobile ? 1 : undefined,
  },
  mainTitle: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 28,
    lineHeight: 32,
    marginBottom: 4,
    color: Colors.light.hereworksBlack600,
  },
  webTitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 4,
    color: Colors.light.hereworksBlack600,
    alignSelf: 'flex-start',
  },
  subTitle: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 24,
    color: Colors.light.hereworksBlack400,
    marginBottom: 32,
  },
  secondTitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 20,
    lineHeight: 30,
    marginBottom: 4,
    color: Colors.light.hereworksBlack600,
    alignSelf: 'stretch',
  },
  secondSub: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 24,
    color: Colors.light.hereworksBlack400,
    marginBottom: 16,
    alignSelf: 'stretch',
  },
  icon: {
    color: Colors.light.hereworksLightBlue,
    marginEnd: 12,
  },
  scrollContainer: {
    width: '100%',
    maxHeight: 200,
    backgroundColor: '#fff',
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    marginTop: 4,
  },
  placeholder: {
    color: Colors.light.hereworksPrimaryGrey800,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    marginStart: 10,
  },
  dropdownButton: {
    paddingStart: 10,
    paddingVertical: 10,
    borderRadius: 4,
    marginVertical: 4,
  },
  dropdownTitle: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 18,
    marginBottom: 4,
    marginEnd: 20,
    color: Colors.light.hereworksBlack400,
  },
  dropdownView: {
    position: 'absolute',
    top: 40,
    left: 0,
    right: 0,
    zIndex: 5,
    backgroundColor: '#fff',
  },
  inputText: {
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
    alignItems: 'flex-start',
    fontSize: 16,
    backgroundColor: Colors.light.hereworksBackground,
    borderWidth: 1,
    alignContent: 'flex-start',
    marginVertical: 5,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 5,
    verticalAlign: 'top',
    height: 80,
    outlineWidth: 0,
  },
  sendButton: {
    zIndex: 1,
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 10,
    marginTop: 24,
    backgroundColor: Colors.light.hereworksBlue500,
    alignItems: 'center',
  },
  sendText: {
    fontFamily: 'Roboto_400Regular',
    color: '#fff',
    fontSize: 16,
    lineHeight: 24,
  },
  shadow: {
    shadowOpacity: 0.15,
    shadowRadius: 20,
    shadowColor: '#000',
  },
});

const TOPICS: Topic[] = [
  { name: 'Can\'t login' },
  {
    name: 'Location Management',
    issues: [
      { name: 'Can\'t add a location' },
      { name: 'Can\'t edit location' },
      { name: 'Map issue' },
      { name: 'Need help with building settings' },
      { name: 'Need help with priority change' },
      { name: 'Other' },
    ],
  },
  {
    name: 'User Management',
    issues: [
      { name: 'Need help with user invite' },
      { name: 'Need help with Editing a group' },
      { name: 'Need asistance with group notificaitons' },
      { name: 'Other' },
    ],
  },
  {
    name: 'Asset management',
    issues: [
      { name: 'Need help with template creation' },
      { name: 'Need help with additional fields' },
      { name: 'Need help with adding asseet using the mobile app' },
      { name: 'Other' },
    ],
  },
  {
    name: 'Tickets',
    issues: [
      { name: 'Need assistance with changing ticket status' },
      { name: 'Need help to delete a ticket' },
      { name: 'Need help with Message to reporter' },
      { name: 'Other' },
    ],
  },
  {
    name: 'Analytics',
    issues:
      [
        { name: 'Graphs are not displaying data' },
        { name: 'Insights at a glance not working' },
        { name: 'Other' },
      ],
  },
  {
    name: 'How to',
    issues: [
      { name: 'Location Management' },
      { name: 'User Management' },
      { name: 'Asset Management' },
      { name: 'Tickets' },
      { name: 'Analytics' },
    ],
  },
  { name: 'Other' },
];
